import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import screenfull from 'screenfull';
import xdk, { PlayerConstants as Events } from '@accedo/xdk-core';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import styles from './player.scss';
import { actions } from '#/redux/modules/appPlayer';
import {
  updateProfileBookmark,
  updateProfileRecentlyWatched,
} from '#/redux/modules/identity/actions';
import { loadAsset } from '#/redux/modules/brightcove/actions';
import { getAssetData } from '#/redux/modules/brightcove/selectors';
import {
  getPlayerState,
  getVideoPlayTime,
  getPrerollAdDuration,
  getPrerollAdByTime,
  getAdsCount,
  getAdPlayed,
  getAdDetailsLoaded,
  getVideoCaptionEnabled,
  getVideoInterrupted,
  getVideoInterruptionReason,
  getVideoInterruptionTime,
} from '#/redux/modules/appPlayer/selectors';

import { playerConstants, videoAnalytics } from '#/config/constants';
import { envConfig } from '#/config/env';
import PlayerOverlay from '#/components/Player/PlayerOverlay';
import UpNextVideo from './UpNextVideo';
import AdIndicator from './AdIndicator';
import VideoInterrupted from './VideoInterrupted';
import { isWorkstation, isVIZIO, isTizen } from '#/utils/componentStyleConfig';

import ClosedCaption from './ClosedCaption';

const {
  BUFFERING: { PROGRESS },
} = Events;

const { play: resume, seeked: seekEnd } = videoAnalytics.controls;

const {
  setVideoPause,
  setVideoPlay,
  setVideoEnded,
  setVideoFullScreen,
  // setVideoSeek,
  setVideoSeeked,
  setVideoPlayTime,
  setVideoBookmark,
  startBookmarking,
  stopBookmarking,
  sendVideoWatchHistory,
  videoInitiated,
  videoStart,
  videoQualityChange,
  videoComplete,
  videoBufferStart,
  videoControl,
  videoInterruption,
  videoProgress,
  startPlaybackTimer,
  stopPlaybackTimer,
  findAdDetails,
  setAdPlayed,
  setAdDetailsLoaded,
  videoStarted,
  setVideoInterrupted,
} = actions;

function VODPlayer({
  videoId,
  videoTitle,
  videoTime = 0,
  videoTextTracks,
  isFullScreenPlayer = false,
  isBackgroundPlayerFullscreen,
  container,
  withUI = true,
  fromHome = false,
  history,
}) {
  const dispatch = useDispatch();
  const videoContainerRef = useRef();
  const pauseTimerRef = useRef();
  const startedVideo = useRef(false);
  const [triggerResume, setTriggerResume] = useState(false);
  const [mediaUrl, setMediaUrl] = useState();
  const videoInfo = useSelector(state => getAssetData(state));
  const playerState = useSelector(state => getPlayerState(state));
  const ccEnabled = useSelector(getVideoCaptionEnabled);
  const previousStream = useRef('');
  const retryCounter = useRef(0);
  const isVideoInterrupted = useSelector(getVideoInterrupted);
  const videoInterruptionReason = useSelector(getVideoInterruptionReason);
  const videoInterruptionTime = useSelector(getVideoInterruptionTime);
  const ccEnabledRef = useRef(ccEnabled);
  const interruptionStateRef = useRef(isVideoInterrupted);
  const interruptionReasonRef = useRef(videoInterruptionReason);
  const interruptionTimeRef = useRef(videoInterruptionTime);

  useEffect(() => {
    ccEnabledRef.current = ccEnabled;
  }, [ccEnabled]);

  useEffect(() => {
    interruptionStateRef.current = isVideoInterrupted;
    interruptionReasonRef.current = videoInterruptionReason;
    interruptionTimeRef.current = videoInterruptionTime;
  }, [isVideoInterrupted, videoInterruptionReason, videoInterruptionTime]);

  // Clean up useEffect
  useEffect(() => {
    // Place inside of the return function any value that needs to be cleaned.
    return () => {
      // remove video started
      dispatch(videoStarted(false));
      // Set player interrupted to false
      dispatch(setVideoInterrupted(false));
      // Rewind retry counter
      retryCounter.current = 0;
    };
  }, []);

  // -CUSTSUPP-22354 Clean up video interrupted state when we change videoId- //
  if (previousStream.current !== videoId) {
    dispatch(setVideoInterrupted(false));
  }
  // -***************************************************************- //

  // Video loading
  const [pLoadVideo, setPLoadVideo] = useState(Promise.resolve());

  // Ads
  const [playTime, setPlayTime] = useState(0);
  const [controlsDisabled, setControlsDisabled] = useState(false);
  const { adSequence, adEndTime } = useSelector(
    state => getPrerollAdByTime(state, playTime * 1000),
    shallowEqual,
  );
  const adsCount = useSelector(getAdsCount);
  const prerollAdDuration = useSelector(getPrerollAdDuration);
  const adPlayed = useSelector(getAdPlayed);
  const adDetailsLoaded = useSelector(getAdDetailsLoaded);

  // Playback
  const videoPlayTime = useSelector(getVideoPlayTime);
  const playerInitialResumed = useRef(false);
  const playerFirstPlay = useRef(false);
  const lastVideoId = useRef();
  const hasPassedProgress = useRef({
    ten: false,
    twentyFive: false,
    fifty: false,
    seventyFive: false,
  });
  const [playerControlsContainer, setPlayerControlsContainer] = useState();
  const [seconds, setSeconds] = useState(0);
  const [showUpNext, setShowUpNext] = useState(false);
  const UP_NEXT_TIMER_INTERVAL = 1000;
  const remainingTimeinSec = xdk.media?.__player?._vjs?.remainingTime();
  const closedCaptionURL = videoTextTracks?.find(
    item => item.kind === 'captions',
  )?.src;

  const persistBookmarkProgress = useCallback(() => {
    const currentTime = Number(xdk.media?.getCurrentTime?.()) || 0;
    const normalizedProgress = Math.max(
      0,
      currentTime - prerollAdDuration / 1000,
    );
    if (normalizedProgress > 0) {
      dispatch(updateProfileBookmark(normalizedProgress));
    }
  }, [dispatch, prerollAdDuration]);

  const onPause = useCallback(() => {
    dispatch(setVideoPause());
    persistBookmarkProgress();
    pauseTimerRef.current && clearTimeout(pauseTimerRef.current);
    pauseTimerRef.current = setTimeout(() => {
      dispatch(sendVideoWatchHistory());
      pauseTimerRef.current = null;
    }, playerConstants.WATCH_HISTORY_TIMEOUT); // send watch history after 10 seconds being interrupted
  }, [persistBookmarkProgress]);

  const onPlay = useCallback(() => {
    dispatch(setVideoPlay());

    if (isWorkstation()) {
      // not sure why this is getting called here, don't want to break web so just removing for TV
      xdk.media?.play();
    }

    if (pauseTimerRef.current) {
      clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = null;
    }
  });

  const onTimeUpdate = useCallback(
    ({ time, duration }) => {
      setTriggerResume(true);
      const percentComplete = Math.floor((time / duration) * 100) / 100;
      setPlayTime(time);
      // SSAI
      // Enable player controls if we are past the pre-roll ad duration
      const progressControl =
        xdk.media?.__player._vjs.controlBar.progressControl;
      if (adDetailsLoaded && time * 1000 > prerollAdDuration) {
        !progressControl.enabled() && progressControl.enable();
        setControlsDisabled(false);
      }
      // Set video play time for watch history if past pre-roll ad duration
      if (!videoPlayTime && time * 1000 > prerollAdDuration) {
        dispatch(setVideoPlayTime());
      }
      if (videoPlayTime && time * 1000 < prerollAdDuration) {
        dispatch(sendVideoWatchHistory());
      }
      if (adDetailsLoaded && !adPlayed && time * 1000 > prerollAdDuration) {
        dispatch(setAdPlayed(true));
      }

      // Handle very short videos missing events with small tolerances ~40 seconds
      if (
        percentComplete >= 0.75 &&
        percentComplete <= 0.76 &&
        !hasPassedProgress.current.seventyFive
      ) {
        hasPassedProgress.current.seventyFive = true;
        dispatch(videoProgress(duration * 0.75));
        return;
      }

      if (
        percentComplete >= 0.5 &&
        percentComplete <= 0.51 &&
        !hasPassedProgress.current.fifty
      ) {
        hasPassedProgress.current.fifty = true;
        dispatch(videoProgress(duration * 0.5));
        return;
      }

      if (
        percentComplete >= 0.25 &&
        percentComplete <= 0.26 &&
        !hasPassedProgress.current.twentyFive
      ) {
        hasPassedProgress.current.twentyFive = true;
        dispatch(videoProgress(duration * 0.25));
        return;
      }

      if (
        percentComplete >= 0.1 &&
        percentComplete <= 0.11 &&
        !hasPassedProgress.current.ten
      ) {
        hasPassedProgress.current.ten = true;
        dispatch(videoProgress(duration * 0.1));
      }
    },
    [adDetailsLoaded, prerollAdDuration, videoPlayTime, adPlayed],
  );

  const onBuffering = useCallback(({ type }) => {
    if (type === PROGRESS) {
      dispatch(videoBufferStart());
    }
  }, []);

  const onEnded = useCallback(() => {
    dispatch(videoComplete(videoInfo.reference_id));
    // When video ends, stop behaviour seeks back to 0
    // Remove listener here to stop buffering events post finish
    xdk.environment.removeEventListener(
      xdk.environment.MEDIA.BUFFERING,
      onBuffering,
    );
    // send onEnd event and reset flag
    dispatch(setVideoEnded());
    dispatch(setVideoEnded(false));
  });

  // const onSeeking = useCallback(() => {
  //   dispatch(setVideoSeek());
  // });

  const onSeeked = useCallback(() => {
    dispatch(setVideoSeeked());
  });

  const onFullscreen = useCallback(() => {
    if (!isFullScreenPlayer) {
      const video = videoContainerRef.current.firstChild;
      dispatch(setVideoFullScreen());
      if (screenfull.isFullscreen) {
        video.classList.add(playerConstants.FULL_SCREEN_CLASS);
        dispatch(videoControl(videoAnalytics.controls.enterFullscreen));
      } else {
        video.classList.remove(playerConstants.FULL_SCREEN_CLASS);
        dispatch(videoControl(videoAnalytics.controls.exitFullscreen));
      }
    }
  });

  const onQualityChanged = useCallback(() => {
    if (startedVideo.current) {
      const defaultHeight = xdk.media?.__player?._playerObject?.clientHeight;
      const defaultWidth = xdk.media?.__player?._playerObject?.clientWidth;
      const qualityLevels = xdk.media?.getBitrates();
      qualityLevels
        ? dispatch(
            videoQualityChange({
              width: qualityLevels?.width,
              height: qualityLevels?.height,
              bitrate: qualityLevels?.bitrate,
            }),
          )
        : dispatch(
            videoQualityChange({
              width: defaultWidth,
              height: defaultHeight,
              bitrate: 0,
            }),
          );
    }
  });

  const onError = useCallback(({ error }) => {
    dispatch(videoInterruption(error?.e, error?.currentTime));
  });

  const onStateChanged = useCallback(async ({ from, to }) => {
    const MEDIASTATE = xdk.CONSTANT.MEDIA.STATE;
    if (from === MEDIASTATE.CONNECTING && to !== MEDIASTATE.STOPPED) {
      // after content is loaded we need to add to recently watched, start bookmarking timer.
      dispatch(updateProfileRecentlyWatched());
      dispatch(startBookmarking());
      if (ccEnabledRef.current) {
        xdk.media?.showSubtitle?.(0);
      }

      const defaultHeight = xdk.media?.__player?._playerObject?.clientHeight;
      const defaultWidth = xdk.media?.__player?._playerObject?.clientWidth;
      // delay by 1s to get defined bitrate/dimensions
      setTimeout(() => {
        const qualityLevels = xdk.media?.getBitrates();
        qualityLevels
          ? dispatch(
              videoStart({
                width: qualityLevels?.width,
                height: qualityLevels?.height,
                bitrate: qualityLevels?.bitrate,
              }),
            )
          : dispatch(
              videoStart({
                width: defaultWidth,
                height: defaultHeight,
                bitrate: 0,
              }),
            );
      }, 1000);

      startedVideo.current = true;
    }
    if (to === MEDIASTATE.PLAYING) {
      if (interruptionStateRef.current) {
        dispatch(setVideoInterrupted(false));
      }
      dispatch(startPlaybackTimer());
    } else if (to === MEDIASTATE.STOPPED || to === MEDIASTATE.PAUSED) {
      dispatch(stopPlaybackTimer());
    }
  }, []);

  const tryResumeAfterReconnect = useCallback(() => {
    if (!isTizen()) {
      return;
    }
    if (
      !interruptionStateRef.current ||
      interruptionReasonRef.current !== videoAnalytics.events.NETWORK_ERROR
    ) {
      return;
    }
    const resumeTime = Number(interruptionTimeRef.current) || 0;
    dispatch(setVideoInterrupted(false));
    setTimeout(() => {
      if (resumeTime > 0) {
        xdk.media?.seek?.(resumeTime);
      }
      xdk.media?.play?.();
    }, 300);
  }, []);

  useEffect(() => {
    if (playerState && startedVideo.current) {
      if (playerInitialResumed.current && playerFirstPlay.current) {
        dispatch(videoControl(playerState));
        return;
      }

      if (playerState === seekEnd || playerState === resume) {
        playerInitialResumed.current = true;
        playerFirstPlay.current = true;
      }
    }
  }, [playerState]);

  useEffect(() => {
    return () => {
      if (!isFullScreenPlayer && xdk.media?.getState() !== 'stopped') {
        dispatch(setVideoBookmark());
      }
      persistBookmarkProgress();

      pauseTimerRef.current && clearTimeout(pauseTimerRef.current);
      dispatch(stopBookmarking());
      dispatch(sendVideoWatchHistory());
      xdk.media?.deinit();
      setMediaUrl(xdk.media.getURL());
    };
  }, [persistBookmarkProgress]);

  useEffect(() => {
    const adConfigId = (() => {
      if (fromHome && !isBackgroundPlayerFullscreen) {
        return null;
      }
      if (isTizen()) {
        return envConfig.adConfigIdTizen;
      }
      if (isVIZIO()) {
        return envConfig.adConfigIdVizio;
      }
      return envConfig.adConfigId;
    })();
    if (videoId && videoId !== Number(videoInfo?.id)) {
      pauseTimerRef.current && clearTimeout(pauseTimerRef.current);
      persistBookmarkProgress();
      dispatch(stopBookmarking());
      dispatch(sendVideoWatchHistory());
      dispatch(loadAsset([{ videoId, adConfigId }]));
    }
  }, [videoId, persistBookmarkProgress]);

  useEffect(() => {
    if (triggerResume) {
      // Make sure we don't try to resume when video is the same as before
      if (videoTime && videoId !== lastVideoId.current) {
        playerInitialResumed.current = false;
        xdk.media?.seek(videoTime);
        lastVideoId.current = videoId;
      } else {
        playerInitialResumed.current = true;
      }
    }
  }, [triggerResume, videoTime, videoId]);

  useEffect(() => {
    let video;
    const pNextVideoLoading = (async () => {
      await pLoadVideo;
      if (videoId === Number(videoInfo?.id) && videoContainerRef.current) {
        startedVideo.current = false;
        playerFirstPlay.current = false;
        playerInitialResumed.current = false;
        hasPassedProgress.current = {
          ten: (videoTime * 1000) / videoInfo.duration >= 0.1,
          twentyFive: (videoTime * 1000) / videoInfo.duration >= 0.25,
          fifty: (videoTime * 1000) / videoInfo.duration >= 0.5,
          seventyFive: (videoTime * 1000) / videoInfo.duration >= 0.75,
        };

        // const testVmap = 'http://localhost:8080/vmap2.xml';
        const { src: brightcoveSource, vmap } = videoInfo.sources.find(
          item =>
            item.type === 'application/x-mpegURL' &&
            item.src.indexOf('https') > -1,
        ); // use HLS sources

        const source = vmap
          ? await dispatch(findAdDetails(vmap, videoInfo.duration))
          : brightcoveSource;

        if (!vmap) {
          dispatch(setAdDetailsLoaded(true));
        }

        const thumbnailTrack = videoInfo.text_tracks.find(
          item => item.label === 'thumbnails' && item.src.indexOf('https') > -1,
        )?.src;
        const ccTrack = videoInfo.text_tracks.find(
          item => item.kind === 'captions',
        );

        if (ccTrack?.src.indexOf('https') === -1 && ccTrack.sources?.length) {
          const httpsTrack = ccTrack.sources.find(
            track => track.src.indexOf('https') > -1,
          )?.src;

          if (httpsTrack) {
            ccTrack.src = httpsTrack;
          } else {
            ccTrack.src = null;
          }
        }

        // Prevent duplicate analytics calls
        if (previousStream.current !== videoId) {
          dispatch(
            videoInitiated({
              streamType: videoAnalytics.streamType.VOD,
              playerType: isFullScreenPlayer
                ? videoAnalytics.player.FULLSCREEN
                : videoAnalytics.player.BACKGROUND,
              bitrate: 0,
            }),
          );
          previousStream.current = videoId;
        }

        if (videoContainerRef.current) {
          xdk.media?.load(source, {
            parentNode: videoContainerRef.current,
            sec: videoTime,
            title: videoTitle,
            live: false,
            thumbnailTrack,
            ccTrack,
          });
          setMediaUrl(xdk.media.getURL());

          // Ads
          xdk.media?.__player._vjs.controlBar.progressControl.disable();
          setControlsDisabled(true);

          video = videoContainerRef.current.firstChild.firstChild;
          video.addEventListener('pause', onPause);
          video.addEventListener('play', onPlay);
          video.addEventListener('ended', onEnded);
          // video.addEventListener('seeking', onSeeking);
          video.addEventListener('seeked', onSeeked);
          xdk.environment.addEventListener(
            xdk.environment.MEDIA.STATE_CHANGED,
            onStateChanged,
          );
          xdk.environment.addEventListener(
            xdk.environment.MEDIA.BUFFERING,
            onBuffering,
          );
          xdk.environment.addEventListener(
            xdk.environment.MEDIA.ERROR,
            onError,
          );
          xdk.media?.__player._vjs
            .qualityLevels()
            .on('change', onQualityChanged);
          screenfull.on('change', onFullscreen);

          videoContainerRef.current.firstChild.classList.add(
            styles.vjsPlayerCustom,
          );

          if (isFullScreenPlayer || document.fullscreenElement) {
            dispatch(setVideoFullScreen({ forceFullScreen: true }));
            videoContainerRef.current.firstChild.classList.add(
              playerConstants.FULL_SCREEN_CLASS,
            );
          } else {
            dispatch(setVideoFullScreen({ resetFullScreen: true }));
          }

          const playerControlsEl = videoContainerRef.current.getElementsByClassName(
            playerConstants.PLAYER_CONTROLS_CONTAINER_CLASS,
          )[0];
          setPlayerControlsContainer(playerControlsEl);
        }
      }
    })();
    setPLoadVideo(pNextVideoLoading);

    return () => {
      video?.removeEventListener('pause', onPause);
      video?.removeEventListener('play', onPlay);
      video?.removeEventListener('ended', onEnded);
      // video?.removeEventListener('seeking', onSeeking);
      video?.removeEventListener('seeked', onSeeked);
      xdk.environment.removeEventListener(
        xdk.environment.MEDIA.STATE_CHANGED,
        onStateChanged,
      );
      xdk.environment.removeEventListener(
        xdk.environment.MEDIA.BUFFERING,
        onBuffering,
      );
      xdk.environment.removeEventListener(xdk.environment.MEDIA.ERROR, onError);
      xdk.media?.__player?._vjs
        ?.qualityLevels()
        .off('change', onQualityChanged);
      screenfull.off('change', onFullscreen);
      dispatch(setAdPlayed(false));
      dispatch(setAdDetailsLoaded(false));
    };
  }, [videoId, videoInfo, videoContainerRef.current]);

  useEffect(() => {
    if (mediaUrl) {
      xdk.environment.addEventListener(
        xdk.environment.MEDIA.TIME_UPDATE,
        onTimeUpdate,
      );
    }

    return () => {
      xdk.environment.removeEventListener(
        xdk.environment.MEDIA.TIME_UPDATE,
        onTimeUpdate,
      );
    };
  }, [mediaUrl, adDetailsLoaded, prerollAdDuration, videoPlayTime, adPlayed]);

  useEffect(() => {
    if (!remainingTimeinSec) {
      return;
    }

    if (!isWorkstation() && isBackgroundPlayerFullscreen) {
      const secondsInterval = setInterval(() => {
        setSeconds(sec => sec + 1);
      }, UP_NEXT_TIMER_INTERVAL);

      if (remainingTimeinSec <= 10) {
        setShowUpNext(true);
      }

      return () => {
        clearInterval(secondsInterval);

        setShowUpNext(false);
      };
    }
  }, [seconds, remainingTimeinSec, showUpNext, isBackgroundPlayerFullscreen]);

  useEffect(() => {
    if (!isTizen()) {
      return undefined;
    }
    const onOffline = () => {
      dispatch(
        setVideoInterrupted({
          isInterrupted: true,
          reason: videoAnalytics.events.NETWORK_ERROR,
          time: xdk.media?.getCurrentTime?.() || 0,
        }),
      );
    };
    const onOnline = () => {
      tryResumeAfterReconnect();
    };

    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);

    return () => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    };
  }, [dispatch, tryResumeAfterReconnect]);

  const adIndicator =
    adDetailsLoaded && prerollAdDuration && !adPlayed ? (
      <AdIndicator {...{ playTime, adSequence, adEndTime, adsCount }} />
    ) : null;

  return (
    <div
      className={`${
        isFullScreenPlayer
          ? styles.fullscreenVideoWrapper
          : styles.homeVideoWrapper
      }`}
    >
      {adIndicator}
      {!isVideoInterrupted ? (
        <div style={{ pointerEvents: 'none' }} ref={videoContainerRef} />
      ) : (
        <VideoInterrupted
          interruptionReason={videoInterruptionReason}
          onRetry={tryResumeAfterReconnect}
        />
      )}
      <UpNextVideo
        remainingTimeinSec={remainingTimeinSec}
        fromHome={fromHome}
        showUpNext={showUpNext}
        withUI={withUI}
        history={history}
      />
      {withUI && playerControlsContainer && (
        <PlayerOverlay
          videoId={videoId}
          container={playerControlsContainer}
          fullscreenContainer={container}
          fromHome={fromHome}
          playerState={playerState}
          isBackgroundPlayerFullscreen={isBackgroundPlayerFullscreen}
          controlsDisabled={controlsDisabled}
        />
      )}
      {closedCaptionURL && ccEnabled && (
        <ClosedCaption
          adDuration={!!prerollAdDuration && !adPlayed ? prerollAdDuration : 0}
          currentTime={playTime}
          captionUrl={closedCaptionURL}
          isHidden={playerControlsContainer?.style?.opacity === '1'}
        />
      )}
    </div>
  );
}

VODPlayer.propTypes = {
  videoId: PropTypes.number,
  videoTitle: PropTypes.string,
  videoTime: PropTypes.number,
  videoTextTracks: PropTypes.object,
  isFullScreenPlayer: PropTypes.bool,
  isBackgroundPlayerFullscreen: PropTypes.bool,
  container: PropTypes.object,
  withUI: PropTypes.bool,
  fromHome: PropTypes.bool,
  history: PropTypes.object,
};

export default VODPlayer;
